/**
 * Bookloop Search Modal
 * Triggers a modal search dialog from any button with class 'js-open-bookloop-search'
 * Reuses the predictive search autocomplete functionality
 */

(function() {
  'use strict';

  const TRIGGER_CLASS = 'js-open-bookloop-search';
  let modal = null;
  let isInitialized = false;

  function createModal() {
    const modalHTML = `
      <div class="bookloop-search-modal__overlay" id="bookloop-search-overlay"></div>
      <div class="bookloop-search-modal" id="bookloop-search-modal" role="dialog" aria-modal="true" aria-label="Search books">
        <div class="bookloop-search-modal__content">
          <button type="button" class="bookloop-search-modal__close" id="bookloop-search-close" aria-label="Close search">
            <svg class="icon icon-close" aria-hidden="true" focusable="false">
              <use href="#icon-close"></use>
            </svg>
          </button>

          <div class="bookloop-search-modal__search-wrapper">
            <img src="https://cdn.shopify.com/s/files/1/0949/3636/7411/files/booklooplogo.png?v=1783226s129" style="width:200px"/>
            <h2 class="bookloop-search-modal__title">Search for a book to sell</h2>

            <predictive-search class="bookloop-search-form" data-loading-text="Loading...">
              <form action="/search" method="get" role="search" class="bookloop-search bookloop-search-form">
                <div class="field">
                  <input
                    class="bookloop-search__input field__input"
                    id="bookloop-search-input"
                    type="search"
                    name="q"
                    placeholder="Search by title, author, or ISBN..."
                    role="combobox"
                    aria-expanded="false"
                    aria-owns="bookloop-predictive-search-results"
                    aria-controls="bookloop-predictive-search-results"
                    aria-haspopup="listbox"
                    aria-autocomplete="list"
                    autocorrect="off"
                    autocomplete="off"
                    autocapitalize="off"
                    spellcheck="false"
                  >
                  <label class="field__label" for="bookloop-search-input">Search</label>
                  <input type="hidden" name="options[prefix]" value="last">

                  <button
                    type="reset"
                    class="reset__button field__button hidden"
                    id="bookloop-search-reset"
                    aria-label="Reset search"
                  >
                    <svg class="icon icon-close" aria-hidden="true" focusable="false">
                      <use xlink:href="#icon-reset"></use>
                    </svg>
                  </button>

                  <button class="bookloop-search__button field__button" aria-label="Search">
                    <svg class="icon icon-search" aria-hidden="true" focusable="false">
                      <use href="#icon-search"></use>
                    </svg>
                  </button>
                </div>

                <div class="bookloop-predictive-search predictive-search" tabindex="-1" id="bookloop-predictive-search" data-predictive-search>
                  <div class="predictive-search__loading-state">
                    <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                      <circle class="spinner__line" fill="none" cx="33" cy="33" r="30"></circle>
                    </svg>
                  </div>
                </div>

                <span class="predictive-search-status visually-hidden" role="status" aria-hidden="true"></span>
              </form>
            </predictive-search>
          </div>
        </div>
      </div>
    `;

    const wrapper = document.createElement('div');
    wrapper.id = 'bookloop-search-modal-wrapper';
    wrapper.innerHTML = modalHTML;
    document.body.appendChild(wrapper);

    return wrapper.querySelector('#bookloop-search-modal');
  }

  function openModal() {
    if (!modal) {
      modal = createModal();
    }

    modal.parentElement.style.display = 'block';
    modal.classList.add('active');

    // Focus the search input
    const input = modal.querySelector('#bookloop-search-input');
    if (input) {
      setTimeout(() => input.focus(), 100);
    }
  }

  function closeModal() {
    if (modal) {
      modal.parentElement.style.display = 'none';
      modal.classList.remove('active');

      // Clear the search input
      const input = modal.querySelector('#bookloop-search-input');
      if (input) {
        input.value = '';
      }
    }
  }

  function setupEventListeners() {
    // Delegate click handler for trigger buttons
    document.addEventListener('click', (e) => {
      if (e.target.closest('.' + TRIGGER_CLASS)) {
        openModal();
      }
    });

    // Close button
    document.addEventListener('click', (e) => {
      if (e.target.closest('#bookloop-search-close')) {
        closeModal();
      }
    });

    // Overlay click
    document.addEventListener('click', (e) => {
      if (e.target.id === 'bookloop-search-overlay') {
        closeModal();
      }
    });

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal && modal.parentElement.style.display !== 'none') {
        closeModal();
      }
    });

    // Form submission - redirect to bookloop listing page
    document.addEventListener('submit', (e) => {
      if (e.target.classList.contains('bookloop-search-form')) {
        const searchTerm = e.target.querySelector('#bookloop-search-input').value;
        if (searchTerm) {
          // Redirect to bookloop listing page with ISBN/search term
          window.location.href = `/apps/baloo/bookloop/list?isbn=${encodeURIComponent(searchTerm)}`;
        }
      }
    });
  }

  function init() {
    if (isInitialized) return;

    // Add CSS styles
    const style = document.createElement('style');
    style.textContent = `
      #bookloop-search-modal-wrapper {
        display: none;
      }

      .bookloop-search-modal__overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 999;
      }

      .bookloop-search-modal {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        display: flex;
        align-items: flex-start;
        justify-content: center;
        padding-top: 60px;
        z-index: 1000;
        background: rgba(0, 0, 0, 0.3);
      }

      .bookloop-search-modal__content {
        position: relative;
        background: white;
        border-radius: 8px;
        width: 90%;
        max-width: 600px;
        padding: 2rem;
        box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
      }

      .bookloop-search-modal__close {
        position: absolute;
        top: 1rem;
        right: 1rem;
        background: none;
        border: none;
        padding: 0.5rem;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .bookloop-search-modal__close svg {
        width: 24px;
        height: 24px;
      }

      .bookloop-search-modal__title {
        margin: 0 0 1.5rem 0;
        font-size: 1.5rem;
        font-weight: 600;
      }

      .bookloop-search-form {
        width: 100%;
      }

      .bookloop-search__input {
        width: 100%;
        padding: 0.75rem;
        border: 1px solid #ddd;
        border-radius: 4px;
        font-size: 1rem;
      }

      .bookloop-search__input:focus {
        outline: none;
        border-color: #333;
        box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.1);
      }

      .field__label {
        display: none;
      }

      .bookloop-predictive-search {
        margin-top: 1rem;
        display: contents;
      }

      .predictive-search-status {
        display: none;
      }

      @media (max-width: 768px) {
        .bookloop-search-modal {
          padding-top: 0;
          align-items: flex-start;
        }

        .bookloop-search-modal__content {
          width: 100%;
          max-width: 95%;
          border-radius: 8px;
          padding: 1.5rem 1rem;
          margin-top: 200px;
          max-height: 100vh;
          overflow-y: auto;
        }

        .bookloop-search-modal__title {
          font-size: 1.25rem;
          margin-top: 0;
        }
      }
    `;
    document.head.appendChild(style);

    setupEventListeners();
    isInitialized = true;
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
